import { useState } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const agents = [
  {
    role: '课堂结构智能体',
    desc: '识别导入、讲授、案例、互动和总结的时间序列，为后续判断提供课堂地图',
    icon: 'ri-layout-5-line',
    tier: 'input' as const,
    input: '视频 / 音频 / 课件',
    output: '时间轴结构',
  },
  {
    role: '内容理解智能体',
    desc: '提取核心概念、问题链、案例链和价值表达，判断概念关系是否讲透',
    icon: 'ri-book-read-line',
    tier: 'input' as const,
    input: 'ASR文本 / 课件',
    output: '概念网络',
  },
  {
    role: '互动识别智能体',
    desc: '处理提问、等待、回应和追问，识别互动链条的完整度',
    icon: 'ri-chat-smile-2-line',
    tier: 'input' as const,
    input: '声音 / 画面 / 文本',
    output: '互动片段',
  },
  {
    role: '资料关联智能体',
    desc: '连接政策、重大工程、历史事件和数据材料到权威来源与出处',
    icon: 'ri-folder-open-line',
    tier: 'process' as const,
    input: '案例实体 / 数字',
    output: '权威资料',
  },
  {
    role: '评价智能体',
    desc: '将证据映射到观测点和维度，结合专家样本形成专业判断',
    icon: 'ri-scales-3-line',
    tier: 'process' as const,
    input: '多源证据',
    output: '维度评分',
  },
  {
    role: '报告与校验智能体',
    desc: '检查证据完整性，输出可追溯的评价报告与改进建议',
    icon: 'ri-shield-check-line',
    tier: 'output' as const,
    input: '维度结果',
    output: '评价报告',
  },
];

const tierMeta = {
  input: {
    label: '输入层',
    color: 'primary',
    iconBg: 'bg-primary-50 border-primary-200/50',
    iconText: 'text-primary-600',
    tagBg: 'bg-primary-100/60 border-primary-200/30',
    tagText: 'text-primary-700',
    badgeBg: 'bg-primary-50 border-primary-200/30',
    badgeText: 'text-primary-700',
    activeBg: 'bg-primary-50 border-primary-400',
    activeRing: 'ring-primary-300/50',
    bar: 'bg-primary-500',
  },
  process: {
    label: '处理层',
    color: 'accent',
    iconBg: 'bg-accent-50 border-accent-200/50',
    iconText: 'text-accent-600',
    tagBg: 'bg-accent-100/60 border-accent-200/30',
    tagText: 'text-accent-700',
    badgeBg: 'bg-accent-50 border-accent-200/30',
    badgeText: 'text-accent-700',
    activeBg: 'bg-accent-50 border-accent-400',
    activeRing: 'ring-accent-300/50',
    bar: 'bg-accent-500',
  },
  output: {
    label: '输出层',
    color: 'secondary',
    iconBg: 'bg-secondary-50 border-secondary-200/50',
    iconText: 'text-secondary-600',
    tagBg: 'bg-secondary-100/60 border-secondary-200/30',
    tagText: 'text-secondary-700',
    badgeBg: 'bg-secondary-50 border-secondary-200/30',
    badgeText: 'text-secondary-700',
    activeBg: 'bg-secondary-50 border-secondary-400',
    activeRing: 'ring-secondary-300/50',
    bar: 'bg-secondary-500',
  },
};

export default function P25AgentRoundtableNew({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);
  const [activeIndex, setActiveIndex] = useState(0);

  const activeAgent = agents[activeIndex];
  const activeC = tierMeta[activeAgent.tier];

  return (
    <div className="relative min-h-screen flex flex-col overflow-x-hidden overflow-y-auto bg-background-50">
      {/* 背景光斑 */}
      <div ref={orb1Ref} className="absolute top-20 right-20 w-72 h-72 rounded-full bg-primary-100/8 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-16 left-10 w-80 h-80 rounded-full bg-accent-100/6 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 md:px-10 lg:px-16 flex flex-col py-12 md:py-16 lg:py-20 gap-8 md:gap-10">
        {/* 标题 */}
        <div ref={titleRef} className="text-center">
          <span className={`inline-flex items-center gap-2 text-xs font-body font-semibold text-accent-600 uppercase tracking-[0.15em] mb-3 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
            多智能体系统
          </span>
          <h2 className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-3 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            数字教研组：多智能体如何协同评课
          </h2>
          <p className={`text-sm md:text-base text-foreground-500 font-body max-w-2xl mx-auto transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            六个智能体分工明确、相互校验，避免单一模型处理所有问题
          </p>
        </div>

        {/* 六张智能体卡片 */}
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {agents.map((agent, i) => {
            const c = tierMeta[agent.tier];
            const isActive = activeIndex === i;
            return (
              <button
                key={agent.role}
                onClick={() => setActiveIndex(i)}
                className={`relative text-left rounded-xl p-4 md:p-5 border transition-all duration-300 cursor-pointer hover:-translate-y-0.5 ${
                  isActive
                    ? `${c.activeBg} ${c.activeRing} ring-2 shadow-sm`
                    : 'bg-background-50 border-background-200/60 hover:border-background-300'
                }`}
                style={{ transitionDelay: `${350 + i * 80}ms` }}
              >
                {/* 顶部色条 */}
                <div className={`absolute top-0 left-4 right-4 h-0.5 rounded-full ${c.bar} ${isActive ? 'opacity-100' : 'opacity-40'}`} />

                <div className="flex items-start gap-3 mb-2">
                  <div className={`w-10 h-10 rounded-lg ${c.iconBg} flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${isActive ? 'scale-110' : ''}`}>
                    <i className={`${agent.icon} text-lg ${c.iconText}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h4 className={`font-heading text-sm font-bold ${isActive ? 'text-foreground-900' : 'text-foreground-700'}`}>{agent.role}</h4>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-label font-medium ${c.tagBg} ${c.tagText} border whitespace-nowrap`}>
                        {c.label}
                      </span>
                    </div>
                    <p className="text-xs text-foreground-500 font-body leading-snug">{agent.desc}</p>
                  </div>
                </div>

                {/* 输入 → 输出流 */}
                <div className={`flex items-center gap-2 mt-3 pt-2.5 border-t border-background-200/40 transition-opacity duration-300 ${isActive ? 'opacity-100' : 'opacity-60'}`}>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${c.badgeBg} ${c.badgeText} border`}>{agent.input}</span>
                  <i className="ri-arrow-right-line text-xs text-foreground-300" />
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${c.badgeBg} ${c.badgeText} border`}>{agent.output}</span>
                </div>
              </button>
            );
          })}
        </div>

        {/* 详情面板 */}
        <div className={`transition-all duration-700 delay-500 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="rounded-2xl bg-background-100 border border-background-200/50 p-5 md:p-6">
            <div className="flex items-start gap-3 md:gap-4">
              <div className={`w-11 h-11 md:w-12 md:h-12 rounded-xl ${activeC.iconBg} flex items-center justify-center flex-shrink-0`}>
                <i className={`${activeAgent.icon} text-lg md:text-xl ${activeC.iconText}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-heading text-base md:text-lg font-bold text-foreground-900">{activeAgent.role}</h3>
                  <span className={`px-2 py-0.5 rounded text-[11px] font-label font-medium ${activeC.tagBg} ${activeC.tagText} border`}>
                    {activeC.label}
                  </span>
                </div>
                <p className="text-sm text-foreground-600 font-body leading-relaxed mb-3">{activeAgent.desc}</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-foreground-400 font-body">输入：</span>
                  <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${activeC.badgeBg} ${activeC.badgeText} border`}>{activeAgent.input}</span>
                  <i className="ri-arrow-right-line text-xs text-foreground-300" />
                  <span className="text-xs text-foreground-400 font-body">输出：</span>
                  <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${activeC.badgeBg} ${activeC.badgeText} border`}>{activeAgent.output}</span>
                </div>
              </div>
            </div>

            {/* 协同机制补充 */}
            <div className="mt-4 pt-4 border-t border-background-200/40">
              <div className="flex items-start gap-2">
                <div className="w-6 h-6 rounded-md bg-accent-50 border border-accent-200/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-links-line text-xs text-accent-500" />
                </div>
                <p className="text-xs md:text-sm text-foreground-500 font-body leading-relaxed">
                  <span className="font-semibold text-foreground-700">相互约束：</span>
                  视觉智能体看到学生围坐不能直接得出"互动充分"，还需声音智能体确认回应、文本智能体确认回应进入后续教学。
                  多条证据指向一致时，系统才形成较强判断。
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}